import {
  addCivilDays,
  civilDateFromIso,
  intervalToDays,
} from '@/lib/treatmentCourseWizard';
import type { IntervalUnit } from '@/types/treatmentCourses';

export type CourseIntervalWindow = {
  startCivil: string;
  endCivil: string;
  startIso: string;
  endIso: string;
  centerCivil: string;
  intervalDays: number;
  toleranceDays: number;
};

export function computeCourseIntervalWindow(input: {
  previousStartsAt?: string | null;
  intervalValue?: number | null;
  intervalUnit?: IntervalUnit | null;
  toleranceValue?: number | null;
  toleranceUnit?: IntervalUnit | null;
}): CourseIntervalWindow | null {
  const previousCivil = civilDateFromIso(input.previousStartsAt ?? null);
  const intervalDays = intervalToDays(input.intervalValue ?? 0, input.intervalUnit ?? 'days');
  if (!previousCivil || intervalDays <= 0) return null;
  const toleranceDays = intervalToDays(
    input.toleranceValue ?? 0,
    input.toleranceUnit ?? 'days',
  );
  const centerCivil = addCivilDays(previousCivil, intervalDays);
  const startCivil = addCivilDays(centerCivil, -toleranceDays);
  const endCivil = addCivilDays(centerCivil, toleranceDays);
  return {
    startCivil,
    endCivil,
    startIso: `${startCivil}T00:00:00`,
    endIso: `${endCivil}T23:59:59`,
    centerCivil,
    intervalDays,
    toleranceDays,
  };
}

export function isCivilInCourseWindow(
  civil: string | null | undefined,
  window: CourseIntervalWindow | null,
): boolean {
  if (!civil || !window) return false;
  return civil >= window.startCivil && civil <= window.endCivil;
}

export function isInstantInCourseWindow(
  iso: string | null | undefined,
  window: CourseIntervalWindow | null,
): boolean {
  return isCivilInCourseWindow(civilDateFromIso(iso ?? null), window);
}

export function courseWindowCivils(window: CourseIntervalWindow): string[] {
  const days: string[] = [];
  let cursor = window.startCivil;
  for (let i = 0; i < 62; i += 1) {
    days.push(cursor);
    if (cursor === window.endCivil) break;
    cursor = addCivilDays(cursor, 1);
  }
  return days;
}

export function mondayOfCivil(civil: string): string {
  const date = new Date(`${civil}T12:00:00`);
  if (Number.isNaN(date.getTime())) return civil;
  const weekday = date.getDay();
  const offset = weekday === 0 ? -6 : 1 - weekday;
  date.setDate(date.getDate() + offset);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function addCivilWeeks(civil: string, weeks: number): string {
  return addCivilDays(civil, weeks * 7);
}

export function weekCivils(mondayCivil: string, days = 5): string[] {
  const count = Math.max(1, Math.min(7, days));
  return Array.from({ length: count }, (_, index) => addCivilDays(mondayCivil, index));
}

export function daysOutsideCourseWindow(
  civil: string | null | undefined,
  window: CourseIntervalWindow | null,
): number | null {
  if (!civil || !window) return null;
  if (isCivilInCourseWindow(civil, window)) return 0;
  if (civil < window.startCivil) {
    return Math.round(
      (Date.parse(`${window.startCivil}T12:00:00`) - Date.parse(`${civil}T12:00:00`)) /
        86_400_000,
    );
  }
  return Math.round(
    (Date.parse(`${civil}T12:00:00`) - Date.parse(`${window.endCivil}T12:00:00`)) /
      86_400_000,
  );
}
